import React from "react";
import Card from "../../../components/common/card/card";
import Dropdown from "../../../components/common/dropdown/dropdown";
import Button from "../../../components/common/button/button";
import HorizontalScrollableList, { ListItem } from "../../../components/common/horizontalScrollableList/horizontalScrollableList";
import WeightSlider from "../../../components/common/weight-slider/weight-slider";
import { ErrorInfoIconDark, ErrorWarningIcon, YesIcon, NoIcon, RightArrowIcon } from "../../../assets/Icons";
import { IIconBasicProps } from "../../../assets/Icons/IIconBasicProps";
import { KaratHelpAccordion, PurityHelpAccordion, GoldTextAreaAttribute } from "../GoldComponents";
import { getAttrDisplayName, getCategoryDisplayName } from "../ez-quote.helpers";
import { Attribute, Category, Condition, SelectedAttribute, Item, OptionType } from "../ez-quote.types";
import { useTranslation } from "../../../contexts/TranslationContext";

interface AttributesStepProps {
  attributes: Attribute[];
  currentItem: {
    category: Category | null;
    attributes: { [key: string]: SelectedAttribute };
    condition: Condition | null;
    parentCategoryName?: string;
    goldTypeName?: string;
    itemTypeLabel?: string;
  };
  goldCurrentAttributeIndex: number;
  goldParentCategory: Category | null;
  selectedGoldItemType: OptionType | null;
  currentStep: number;
  attributeError: string | null;
  setattributeError: (v: string | null) => void;
  openDropdownId: string | null;
  setOpenDropdownId: (id: string | null) => void;
  dropdownResetCounter: number;
  manufacturerOptions: { coreValue: string; coreValueID: string }[];
  modelOptions: { coreValue: string; coreValueID: string }[];
  handleAttributeSelect: (name: string, opt: SelectedAttribute) => void;
  clearAttributeValues: (name: string) => void;
  handleContinue: () => void;
  getIconComponent: (name: string | null) => React.FC<IIconBasicProps>;
  brand: string | null;
  // For handleGoldAttributeContinue:
  setCurrentItem: React.Dispatch<React.SetStateAction<any>>;
  setItems: React.Dispatch<React.SetStateAction<Item[]>>;
  setDraftItem: React.Dispatch<React.SetStateAction<any>>;
  setIsEditing: (v: boolean) => void;
  setCurrentStep: (step: number) => void;
  setGoldCurrentAttributeIndex: React.Dispatch<React.SetStateAction<number>>;
  draftItem: any;
  isEditing: boolean;
}

const AttributesStep: React.FC<AttributesStepProps> = ({
  attributes,
  currentItem,
  goldCurrentAttributeIndex,
  goldParentCategory,
  selectedGoldItemType,
  currentStep,
  attributeError,
  setattributeError,
  openDropdownId,
  setOpenDropdownId,
  dropdownResetCounter,
  manufacturerOptions,
  modelOptions,
  handleAttributeSelect,
  clearAttributeValues,
  handleContinue,
  getIconComponent,
  brand: _brand,
  setCurrentItem,
  setItems,
  setDraftItem,
  setIsEditing,
  setCurrentStep,
  setGoldCurrentAttributeIndex,
  draftItem,
  isEditing: _isEditing,
}) => {
  const t = useTranslation();

  const handleGoldAttributeContinue = (attrName: string, value: SelectedAttribute) => {
    const nextIdx = goldCurrentAttributeIndex + 1;

    if (nextIdx >= attributes.length) {
      // Last Gold attribute — format value and build item directly (no conditions step)
      const isTextArea = attributes.find((a) => a.coreAttributeName === attrName)?.displayType === "TEXT_AREA";
      const formattedName =
        isTextArea || value.id === "other"
          ? value.name
          : value.name.split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
      const finalAttrValue: SelectedAttribute = { ...value, name: formattedName };
      const finalAttributes = {
        ...currentItem.attributes,
        [attrName]: finalAttrValue,
      };
      const textAreaKeys = attributes
        .filter((a) => a.displayType === "TEXT_AREA")
        .map((a) => a.coreAttributeName);
      const newItem: Item = {
        id: `${currentItem.category!.category_id}-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
        category: currentItem.category!,
        attributes: finalAttributes,
        condition: null,
        estimate: "",
        textAreaAttributeKeys: textAreaKeys,
        parentCategoryName: currentItem.parentCategoryName ?? "Jewelry",
        rootCategoryName: currentItem.parentCategoryName ?? "Jewelry",
        goldTypeName: currentItem.goldTypeName ?? goldParentCategory?.category_name,
        itemTypeLabel: selectedGoldItemType?.key === "other"
          ? t.shared.otherNotSure
          : (!goldParentCategory?.childCategories?.length && selectedGoldItemType)
            ? selectedGoldItemType.value
            : undefined,
        ...(selectedGoldItemType?.key === "other" && {
          apiCategoryId: 700001,
          apiCategoryName: "Jewelry Accessories",
        }),
        goldParentCategory: goldParentCategory ?? undefined,
      };
      setCurrentItem((prev: any) => ({ ...prev, attributes: finalAttributes }));
      setItems((prev) => [...prev, newItem]);
      setDraftItem(newItem as typeof draftItem);
      setIsEditing(false);
      setTimeout(() => { setCurrentStep(5); window.scrollTo({ top: 0, behavior: "smooth" }); }, 0);
    } else {
      handleAttributeSelect(attrName, value);
      setGoldCurrentAttributeIndex(nextIdx);
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 0);
    }
  };

  const renderWarningMessage = (selectedOption: OptionType | null) => {
    return selectedOption && selectedOption.key === "other" ? (
      <div className="attribute-warning-message">
        <ErrorWarningIcon />
        <div>{t.attributes.warningMessage}</div>
      </div>
    ) : null;
  };

  const renderAttribute = (attr: Attribute) => {
    if (attr.displayType === "TYPE_AHEAD") {
      const options: OptionType[] =
        attr.coreAttributeName === "Manufacturer"
          ? manufacturerOptions.map((val) => ({
              key: val.coreValueID,
              value:
                val.coreValue.charAt(0).toUpperCase() +
                val.coreValue.slice(1).toLowerCase(),
            }))
          : attr.coreAttributeName === "Model Number"
          ? modelOptions.map((val) => ({
              key: val.coreValueID,
              value: val.coreValue
                .split(" ")
                .map(
                  (word) =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                )
                .join(" "),
            }))
          :
           attr.values?.map((val) => ({
              key: val.coreValue,
              value: val.coreValue
                .split(" ")
                .map(
                  (word) =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                )
                .join(" "),
            })) || [];


      let selectedOption;
      let currentValue: string | undefined;

      currentValue = currentItem.attributes[attr.coreAttributeName]?.id;
      selectedOption =
        options.find((option) => option.key === currentValue) || null;

      if (attr.coreAttributeName === "Model Number") {
        if (!selectedOption) {
          selectedOption = {
            key: currentItem.attributes[attr.coreAttributeName]?.id || "",
            value: currentItem.attributes[attr.coreAttributeName]?.name || "",
          };
        }
      }

      if (
        !selectedOption &&
        currentItem.attributes[attr.coreAttributeName]?.id === "other"
      ) {
        selectedOption = { key: "other", value: t.shared.otherNotSure };
      }

      if (
        attr.coreAttributeName === "Manufacturer" ||
        attr.coreAttributeName === "Model Number" ||
        attr.coreAttributeName === "Processor Type"
      ) {
        return (
          <div key={attr.coreAttributeName} className="attribute">
            <div className="attribute-name">
              {getAttrDisplayName(attr.coreAttributeName, t)}
              <span className="required-asterisk">*</span>
            </div>
            <Dropdown
              id={`${attr.coreAttributeName}-${dropdownResetCounter}`}
              options={options}
              placeholder={t.attributes.searchPlaceholder(getAttrDisplayName(attr.coreAttributeName, t))}
              value={selectedOption}
              onSelect={(option: OptionType) => {
                handleAttributeSelect(attr.coreAttributeName, {
                  id: option.key,
                  name: option.value,
                });
              }}
              openDropdownId={openDropdownId}
              setOpenDropdownId={setOpenDropdownId}
              showOthersOption={attr.coreAttributeName !== "Manufacturer"}
              attributeName={attr.coreAttributeName}
              clearAttributeValues={() =>
                clearAttributeValues(attr.coreAttributeName)
              }
            />
            {renderWarningMessage(selectedOption)}
          </div>
        );
      }
    } else if (
      attr.displayType === "SINGLE_SELECT_LIST" &&
      attr.values &&
      attr.values.length > 0
    ) {
      const uniqueValues = [
        ...Array.from(new Set(attr.values.map((v) => v.coreValue))).map(
          (coreValue) => ({
            id: coreValue,
            label: coreValue,
          })
        ),
        { id: "other", label: t.shared.otherNotSure },
      ];
      const optionValues = uniqueValues.map((item) => ({
        key: item.id,
        value: item.label,
      }));

      const getSelectedOption = (values: { id: string; label: string; }[], attributeId: string | undefined): OptionType | null => {
        return values.map((item) => ({
          key: item.id,
          value: item.label,
        })).find((item) => item.key === attributeId) || null;
      };

      const selectedOptionForSingleSelect = getSelectedOption(uniqueValues, currentItem.attributes[attr.coreAttributeName]?.id);

      return (
        <div key={attr.coreAttributeName} className="attribute">
          <div className="attribute-name">
            {getAttrDisplayName(attr.coreAttributeName, t)}
            <span className="required-asterisk">*</span>
          </div>
          <div className="horizontal-list-wrapper">
            <HorizontalScrollableList
              items={uniqueValues as ListItem[]}
              selectedId={
                currentItem.attributes[attr.coreAttributeName]?.id || ""
              }
              onSelect={(id: string | number) => {
                const idStr = String(id);
                const selected = uniqueValues.find((item) => item.id === idStr);
                if (selected) {
                  handleAttributeSelect(attr.coreAttributeName, {
                    id: selected.id,
                    name: selected.label,
                  });
                }
              }}
            />
          </div>
          {renderWarningMessage(selectedOptionForSingleSelect)}
        </div>
      );
    }

    if (attr.displayType === "TEXT_AREA") {
      const savedText = currentItem.attributes[attr.coreAttributeName]?.id || "";
      return (
        <GoldTextAreaAttribute
          key={attr.coreAttributeName}
          initialValue={savedText}
          onContinue={(text) =>
            handleGoldAttributeContinue(attr.coreAttributeName, { id: text, name: text })
          }
        />
      );
    }

    if (attr.displayType === "SLIDER") {
      const savedId = currentItem.attributes[attr.coreAttributeName]?.id;
      const min = attr.values?.[0]?.coreValue ? Number(attr.values[0].coreValue) : 1;
      const max = attr.values?.[1]?.coreValue ? Number(attr.values[1].coreValue) : 300;
      const initialValue = savedId ? Number(savedId) : min;
      return (
        <WeightSlider
          key={attr.coreAttributeName}
          initialValue={initialValue}
          min={min}
          max={max}
          onContinue={(val) =>
            handleGoldAttributeContinue(attr.coreAttributeName, {
              id: String(val),
              name: String(val),
            })
          }
        />
      );
    }

    return null;
  };

  // Find the index of the last filled attribute
  const lastFilledIndex = attributes.reduce((maxIdx, attr, idx) => {
    const value = currentItem.attributes[attr.coreAttributeName];
    return value && value.id && value.id !== "" ? idx : maxIdx;
  }, -1);

  // Show attributes up to the next one after the last filled
  const nextAttributeIndex = lastFilledIndex + 1;
  const attributesToRender = attributes.slice(0, nextAttributeIndex + 1);

  const hasSlider = attributes.some((a) => a.displayType === "SLIDER");

  // ── Gold mode: one attribute at a time, each with its own Continue ──
  if (hasSlider) {
    const currentAttr = attributes[goldCurrentAttributeIndex];
    if (!currentAttr) return null;

    // SLIDER and TEXT_AREA handle their own Continue button
    if (
      currentAttr.displayType === "SLIDER" ||
      currentAttr.displayType === "TEXT_AREA"
    ) {
      return (
        <div className="attributes-container">{renderAttribute(currentAttr)}</div>
      );
    }

    // Binary SINGLE_SELECT_LIST (e.g. Diamonds Yes/No): vertical cards, auto-advance on tap
    const attrValues = currentAttr.values || [];
    const isBinarySelect =
      attrValues.length > 0 &&
      attrValues.length <= 3 &&
      attrValues.every((v) => ["yes", "no"].includes(v.coreValue.toLowerCase()));
    if (isBinarySelect) {
      return (
        <div className="attributes-container">
          <div className="gold-yesno-list">
            {attrValues.map((v) => {
              const isYes = v.coreValue.toLowerCase() === "yes";
              const isSelected =
                currentItem.attributes[currentAttr.coreAttributeName]?.id?.toLowerCase() ===
                v.coreValue.toLowerCase();
              return (
                <button
                  key={v.coreValue}
                  className={`gold-yesno-card${isSelected ? " selected" : ""}`}
                  onClick={() =>
                    handleGoldAttributeContinue(currentAttr.coreAttributeName, {
                      id: v.coreValue,
                      name: v.coreValue,
                    })
                  }
                >
                  <span className={`gold-yesno-icon${isYes ? " yes" : " no"}`}>
                    {isYes ? <YesIcon width={44} height={44} /> : <NoIcon width={44} height={44} />}
                  </span>
                  <span className="gold-yesno-label">{v.coreValue}</span>
                  <span className="gold-yesno-chevron"><RightArrowIcon width={24} height={24} /></span>
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    // SINGLE_SELECT_LIST with many values (e.g. Karats): horizontal list + Continue
    const selectedId = currentItem.attributes[currentAttr.coreAttributeName]?.id;
    return (
      <div className="attributes-container">
        {renderAttribute(currentAttr)}
        {currentAttr.coreAttributeName.toLowerCase().includes("karat") && goldParentCategory?.category_name === "Gold" && <KaratHelpAccordion />}
        {currentAttr.coreAttributeName.toLowerCase().includes("quality") && goldParentCategory?.category_name !== "Gold" && goldParentCategory && (
          <PurityHelpAccordion metalName={goldParentCategory.category_name} />
        )}
        {attributeError && (
          <div className="attribute-error-message">
            <ErrorInfoIconDark />
            {attributeError}
          </div>
        )}
        <div className="continue-button-container-gold">
          <Button
            label={t.shared.continue}
            styleType="primary"
            disabled={!selectedId}
            onClick={() => {
              if (!selectedId) {
                setattributeError(t.attributes.errorMustSelect(getAttrDisplayName(currentAttr.coreAttributeName, t)));
                return;
              }
              setattributeError(null);
              handleGoldAttributeContinue(
                currentAttr.coreAttributeName,
                currentItem.attributes[currentAttr.coreAttributeName]
              );
            }}
          />
        </div>
      </div>
    );
  }

  // ── Standard mode ──
  return (
    <div className="attributes-container">
      {currentItem.category && (
        <div className="selected-category-card">
          <Card
            title={getCategoryDisplayName(currentItem.category, t)}
            Icon={getIconComponent(currentItem.category.icon_name)}
            className="category-card"
          />
        </div>
      )}
      {attributesToRender.map(renderAttribute)}
      {attributeError && (
        <div className="attribute-error-message">
          <ErrorInfoIconDark />
          {attributeError}
        </div>
      )}

      <div className="attributes-actions">
        {currentStep === 3 &&
          attributes.length > 0 &&
          attributes.every((attr) => {
            const id = currentItem.attributes[attr.coreAttributeName]?.id;
            return id != null && id !== "";
          }) && (
            <div className="continue-button-container">
              <Button
                label={t.shared.continue}
                onClick={handleContinue}
                styleType="primary"
                data-selected-attributes={JSON.stringify(
                  attributes.reduce((acc, attr) => {
                    const selectedValue =
                      currentItem.attributes[attr.coreAttributeName];
                    if (selectedValue && selectedValue.name) {
                      acc[attr.coreAttributeName] = selectedValue.name;
                    }
                    return acc;
                  }, {} as { [key: string]: string })
                )}
              />
            </div>
          )}
      </div>
    </div>
  );
};

export default AttributesStep;
